import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Package } from "lucide-react";

import { get } from "@/lib/api-client";
import { Card, CardBody } from "@/components/ui/Card";
import { Tabs } from "@/components/ui/Tabs";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { StatusPill } from "@/components/ui/StatusPill";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { formatCurrency, formatDateTime } from "@/lib/format";
import type { Order } from "@/types/order";

export function OrdersAdminPage() {
  const [tab, setTab] = useState("all");
  const { data, isLoading } = useQuery({
    queryKey: ["admin-orders", tab],
    queryFn: () => get<Order[]>("/admin/orders", tab === "all" ? {} : { status: tab }),
  });

  const orders = data ?? [];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Orders</h1>
        <p className="text-sm text-surface-500 mt-1">
          System-wide order visibility. Read-only — actions performed through payment queue.
        </p>
      </header>

      <Card>
        <div className="px-5 pt-3">
          <Tabs value={tab} onChange={setTab}
                tabs={[
                  { value: "all",                          label: "All",       count: orders.length },
                  { value: "pending_payment",              label: "Awaiting"   },
                  { value: "payment_verification_pending", label: "Verifying" },
                  { value: "completed",                    label: "Completed" },
                  { value: "rejected",                     label: "Rejected"  },
                  { value: "cancelled",                    label: "Cancelled" },
                ]} />
        </div>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14" />)}
            </div>
          ) : orders.length === 0 ? (
            <EmptyState icon={Package} title="No orders in this view" />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Order ID</TH><TH>Customer</TH><TH>Items</TH>
                  <TH>Total</TH><TH>Payment</TH><TH>Status</TH><TH>Placed</TH>
                </TR>
              </THead>
              <tbody>
                {orders.map((o) => (
                  <TR key={o.order_id} onClick={() => window.location.href = `/admin/orders/${o.order_id}`} className="cursor-pointer hover:bg-surface-50">
                    <TD>
                      <Link to={`/admin/orders/${o.order_id}`}
                            className="font-mono text-xs text-brand-600 font-semibold hover:text-brand-700">
                        {o.order_id}
                      </Link>
                    </TD>
                    <TD className="font-mono text-xs">{o.customer_id}</TD>
                    <TD>{o.items.length}</TD>
                    <TD className="font-semibold">{formatCurrency(o.total)}</TD>
                    <TD><StatusPill status={o.payment_status} /></TD>
                    <TD><StatusPill status={o.status} /></TD>
                    <TD className="text-2xs text-surface-500">{formatDateTime(o.placed_at)}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}